import { useEffect, useRef } from "react";
import { X, Check } from "lucide-react";

export interface KwGroup {
  label: string;
  items: string[];
}

export interface KeywordDropdownProps {
  groups: KwGroup[];
  pending: Set<string>;
  onToggle: (kw: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

export default function KeywordDropdown({
  groups,
  pending,
  onToggle,
  onConfirm,
  onClose,
}: KeywordDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  /* click-outside to close */
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const totalSelected = pending.size;
  const hasAny = groups.some((g) => g.items.length > 0);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 z-[200] mt-2 w-80 rounded-2xl border border-gray-100 bg-white shadow-2xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-bold text-gray-800">Add Keywords</p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Keyword list */}
      <div className="max-h-72 overflow-y-auto">
        {!hasAny && (
          <p className="text-sm text-gray-400 text-center py-8 px-4">
            No keywords yet. Import keywords in Content Hub first.
          </p>
        )}
        {groups.map((group) =>
          group.items.length === 0 ? null : (
            <div key={group.label}>
              <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0">
                {group.label}
              </p>
              {group.items.map((item) => {
                const checked = pending.has(item);
                return (
                  <label
                    key={item}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50/60 cursor-pointer transition-colors select-none"
                  >
                    {/* Custom checkbox */}
                    <div
                      className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        checked
                          ? "bg-indigo-600 border-indigo-600"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {checked && (
                        <Check size={10} strokeWidth={3} className="text-white" />
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(item)}
                      className="sr-only"
                    />
                    <span className="text-sm text-gray-700 leading-snug">{item}</span>
                  </label>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
        <span className="text-xs text-gray-500">
          {totalSelected > 0
            ? `${totalSelected} keyword${totalSelected > 1 ? "s" : ""} selected`
            : "Select keywords to start"}
        </span>
        <button
          onClick={onConfirm}
          disabled={totalSelected === 0}
          className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-colors"
        >
          Confirm →
        </button>
      </div>
    </div>
  );
}
